import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useModal } from "react-modal-hook";
import { usePublicUserLoginMutation } from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import {
  Container,
  ContentSection,
  HeaderSection,
} from "../Common/__styles__/Modal";
import Modal from "../Common/Modal";
import { Title } from "../Common/Typography";
import { LoginForm } from "./__styles__/LoginPage";
import {
  ProvideCredentialsForm,
  ProvideCredentialsFormProps,
} from "./ProvideCredentialsForm";

type UsePublicUserLoginModalProps = {
  onLoginSuccess: () => void;
};

export const usePublicUserLoginModal = ({
  onLoginSuccess,
}: UsePublicUserLoginModalProps) => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal
        onRequestClose={() => {
          hideModal();
        }}
      >
        <PublicUserLoginModalContent
          onLoginSuccess={() => {
            onLoginSuccess();
            hideModal();
          }}
        />
      </Modal>
    ),
    []
  );

  return [showModal, hideModal] as const;
};

const PublicUserLoginModalContent = ({
  onLoginSuccess,
}: {
  onLoginSuccess: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Container width="narrow">
      <HeaderSection>
        <Title size="large" type="regular">
          {t("sign-in-to-your-account")}
        </Title>
      </HeaderSection>
      <ContentSection>
        <ProvideCredentials onLoginSuccess={onLoginSuccess} />
      </ContentSection>
    </Container>
  );
};

export type ProvideCredentialsFormStructure = {
  email?: Maybe<string>;
  password?: Maybe<string>;
};

const ProvideCredentials = ({
  onLoginSuccess,
}: {
  onLoginSuccess: () => void;
}) => {
  const { t } = useTranslation();
  const useFormProps = useForm<ProvideCredentialsFormStructure>();
  const { addErrorToast } = useStatusToasts();
  const [login, { data }] = usePublicUserLoginMutation({
    onCompleted: data => {
      if (data.publicUserLogin.ok) {
        onLoginSuccess();
      }
    },
    onError: () => {
      addErrorToast(t("sign-in-unexpected-network-error"));
    },
  });

  const error = data?.publicUserLogin.error ?? null;

  const handleLogin = async ({
    email,
    password,
  }: Parameters<ProvideCredentialsFormProps["onLogin"]>[0]) => {
    await login({
      variables: { email, password },
    });
  };

  return (
    <FormProvider {...useFormProps}>
      <LoginForm>
        <ProvideCredentialsForm error={error} onLogin={handleLogin} />
      </LoginForm>
    </FormProvider>
  );
};

export default usePublicUserLoginModal;
